import React, { useEffect, useState, useCallback } from "react";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Copy, Trash2, FileText, Languages, Loader2, Inbox, FileDown } from "lucide-react";

export default function History() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [exporting, setExporting] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/history");
      setItems(data);
    } catch (err) {
      console.error("Errore nel caricamento della cronologia", err);
      toast.error("Errore nel caricamento della cronologia");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try {
      await api.delete(`/history/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
      toast.success("Eliminato");
    } catch {
      toast.error("Errore durante l'eliminazione");
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiato");
  };

  const exportOut = async (id, text, format) => {
    if (!text) return;
    setExporting(`${id}-${format}`);
    try {
      const res = await api.post("/translate/export", { text, format }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = format === "docx" ? "traduzione.docx" : "traduzione.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore durante l'esportazione");
    } finally {
      setExporting("");
    }
  };

  const filtered = items.filter((i) => filter === "all" || i.type === filter);

  const renderContent = () => {
    if (loading) {
      return <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-brand-blue" /></div>;
    }
    if (filtered.length === 0) {
      return (
        <div className="text-center py-24">
          <Inbox className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" />
          <p className="text-muted-foreground">Nessuna traduzione ancora.</p>
        </div>
      );
    }
    return (
      <div className="space-y-3">
        {filtered.map((it, i) => (
          <motion.div key={it.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
            data-testid={`history-item-${it.id}`}
            className="rounded-2xl border border-border bg-card p-5 hover:border-brand-blue/30 transition-colors">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-xs font-mono-label uppercase tracking-wider text-muted-foreground">
                {it.type === "document" ? <FileText className="h-3.5 w-3.5 text-brand-pink" /> : <Languages className="h-3.5 w-3.5 text-brand-blue" />}
                {it.source_lang === "auto" ? "auto" : it.source_lang} → {it.target_lang}
                <span className="text-border">·</span>
                <span className="normal-case tracking-normal">{it.tone}</span>
              </div>
              <div className="flex items-center gap-1">
                <button data-testid={`export-pdf-hist-${it.id}`} onClick={() => exportOut(it.id, it.translated_text, "pdf")} disabled={exporting !== ""}
                  className="h-8 px-2.5 rounded-full flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-brand-blue hover:bg-brand-blue/10 transition-colors disabled:opacity-50">
                  {exporting === `${it.id}-pdf` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />} PDF
                </button>
                <button data-testid={`export-docx-hist-${it.id}`} onClick={() => exportOut(it.id, it.translated_text, "docx")} disabled={exporting !== ""}
                  className="h-8 px-2.5 rounded-full flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-brand-blue hover:bg-brand-blue/10 transition-colors disabled:opacity-50">
                  {exporting === `${it.id}-docx` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />} Word
                </button>
                {it.type === "text" && (
                  <button data-testid={`copy-hist-${it.id}`} onClick={() => copy(it.translated_text)}
                    className="h-8 w-8 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                )}
                <button data-testid={`del-hist-${it.id}`} onClick={() => remove(it.id)}
                  className="h-8 w-8 rounded-full flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            {it.type === "document" ? (
              <p className="text-sm font-medium">{it.original_filename}</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <p className="text-sm text-muted-foreground line-clamp-3">{it.source_text}</p>
                <p className="text-sm line-clamp-3">{it.translated_text}</p>
              </div>
            )}
          </motion.div>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-[1000px] mx-auto px-4 md:px-8 py-8">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <h1 className="font-heading text-3xl font-light tracking-tight">Cronologia</h1>
            <p className="text-sm text-muted-foreground mt-1">Tutte le tue traduzioni salvate.</p>
          </div>
          <Tabs value={filter} onValueChange={setFilter}>
            <TabsList className="rounded-full">
              <TabsTrigger value="all" data-testid="filter-all" className="rounded-full">Tutte</TabsTrigger>
              <TabsTrigger value="text" data-testid="filter-text" className="rounded-full">Testo</TabsTrigger>
              <TabsTrigger value="document" data-testid="filter-document" className="rounded-full">Documenti</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {renderContent()}
      </div>
    </div>
  );
}
